import type { OperationRun } from '@foodlista/trigger-vue'
import type { ScopedOperationScope } from '../utils/scoped-operation-contract'
import {
  type TextGenerateInput,
  textGenerateInputSchema,
  textGenerateManifest,
  type TextGenerateOutput,
  textGenerateOutputSchema,
  type TextGenerateProgressPhase,
} from '@foodlista/operations'
import { operationPayloadOf } from '../completion/extract'
import { defineScopedOperation } from '../composables/defineScopedOperation'
import { presentTextGenerate } from '../presentation/presenters'
import { registerOperationPresenter } from '../presentation/registry'
import { registerOperationProgressTranslator } from '../progress/registry'
import { textGenerateScopePayload } from '../scope/payloads'

export interface TextGenerateScope extends ScopedOperationScope {
  scopeKey: string
}

export type TextGenerateRequestInput = Omit<TextGenerateInput, 'scopeKey'>

export interface TextGenerateResult {
  text: TextGenerateOutput
  sourceText: TextGenerateInput['text']
  feature: TextGenerateInput['feature']
  locale: TextGenerateInput['locale']
  maxLength: TextGenerateInput['maxLength']
}

export function parseTextGenerateOutput(run: OperationRun): TextGenerateResult {
  const payload = operationPayloadOf(run)
  const input = textGenerateInputSchema.parse(payload?.data)

  return {
    text: textGenerateOutputSchema.parse(run.output),
    sourceText: input.text,
    feature: input.feature,
    locale: input.locale,
    maxLength: input.maxLength,
  }
}

registerOperationProgressTranslator(
  textGenerateManifest.type,
  (phase, t) => {
    const typed = phase as TextGenerateProgressPhase
    switch (typed.kind) {
      case 'generating':
        return t('operations.boc.progress.textGenerate.generating')
      case 'done':
        return t('operations.boc.progress.textGenerate.done')
    }
  },
)

registerOperationPresenter(textGenerateManifest.type, presentTextGenerate)

export const useTextGenerateOperation = defineScopedOperation<
  TextGenerateRequestInput,
  TextGenerateScope,
  TextGenerateResult
>({
  manifest: textGenerateManifest,
  idempotency: { mode: 'request' },
  scopePayload: textGenerateScopePayload,
  parseOutput: parseTextGenerateOutput,
  enqueue: (input, { orpc, buildEnqueueInput, scope }) =>
    orpc.ai.generateTextAsync({
      ...buildEnqueueInput(input),
      scopeKey: scope.scopeKey,
    }),
})
